import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, Plane, Search, LogIn, MessageCircle, LogOut, Ticket, XCircle, ShieldCheck, CreditCard,
} from "lucide-react";
import { useRazorpay } from "react-razorpay";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Sections5";
import {
    lookupFlightBooking, cancelFlightBooking, customerRegister, customerLogin,
    customerOtpRequest, customerOtpVerify, customerForgot, customerReset,
    customerMe, customerBookings, getCustomerToken, setCustomerToken, formatApiError,
    createFlightPayment, verifyFlightPayment,
} from "@/lib/api";
import { inr, fmtDate, fmtTime } from "@/pages/FlightResultsPage";

const STATE_STYLE = {
    FARE_CONFIRMED: "bg-blue-100 text-blue-700", PAYMENT_PENDING: "bg-amber-100 text-amber-700",
    PAYMENT_CONFIRMED: "bg-blue-100 text-blue-700", BOOKING_PENDING: "bg-amber-100 text-amber-700",
    BOOKING_CONFIRMED: "bg-green-100 text-green-700", TICKET_ISSUANCE_PENDING: "bg-amber-100 text-amber-700",
    TICKETED: "bg-green-100 text-green-700", BOOKING_FAILED: "bg-red-100 text-red-600",
    CANCELLATION_REQUESTED: "bg-orange-100 text-orange-700", REFUND_PENDING: "bg-orange-100 text-orange-700",
    REFUNDED: "bg-stone-200 text-stone-600", PARTIALLY_REFUNDED: "bg-stone-200 text-stone-600",
};
const STATE_LABEL = {
    FARE_CONFIRMED: "Fare confirmed", PAYMENT_PENDING: "Payment pending", PAYMENT_CONFIRMED: "Payment received",
    BOOKING_PENDING: "Booking in progress", BOOKING_CONFIRMED: "Booking confirmed",
    TICKET_ISSUANCE_PENDING: "Ticket issuance pending", TICKETED: "Ticket confirmed",
    BOOKING_FAILED: "Booking failed", CANCELLATION_REQUESTED: "Cancellation requested",
    REFUND_PENDING: "Refund in progress", REFUNDED: "Refunded", PARTIALLY_REFUNDED: "Partially refunded",
};

function BookingCard({ booking, onCancel, onPaid, testid }) {
    const [busy, setBusy] = useState(false);
    const [payBusy, setPayBusy] = useState(false);
    const { Razorpay } = useRazorpay();
    const cancel = async () => {
        if (!window.confirm("Request cancellation for this booking?")) return;
        setBusy(true);
        try {
            const r = await onCancel();
            toast.success(`Status updated: ${STATE_LABEL[r.booking.state] || r.booking.state}`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const canPay = !booking.demo && ["FARE_CONFIRMED", "PAYMENT_PENDING"].includes(booking.state);
    const payNow = async () => {
        setPayBusy(true);
        try {
            const order = await createFlightPayment(booking.reference);
            const rzp = new Razorpay({
                key: order.key_id, amount: order.amount * 100, currency: order.currency,
                name: "GhumneChaley.com", description: `Flight booking ${booking.reference}`,
                order_id: order.order_id,
                theme: { color: "#F26522" },
                handler: async (resp) => {
                    try {
                        const r = await verifyFlightPayment({
                            order_id: resp.razorpay_order_id, payment_id: resp.razorpay_payment_id,
                            signature: resp.razorpay_signature,
                        });
                        onPaid?.(r.booking);
                        toast.success("Payment received — your ticket is being issued");
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                },
                modal: { ondismiss: () => toast("Payment window closed — nothing was charged.") },
            });
            rzp.on("payment.failed", (resp) => toast.error(`Payment failed: ${resp.error?.description || "try another method"}`));
            rzp.open();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally { setPayBusy(false); }
    };
    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm" data-testid={testid}>
            <div className="flex flex-wrap items-center gap-3 justify-between">
                <div>
                    <p className="font-display font-black text-lg text-navy">{booking.reference}</p>
                    <p className="text-xs text-stone-400">Booked {fmtDate(booking.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                    {booking.demo && <span className="rounded-full bg-gold/20 border border-gold/60 px-2.5 py-1 text-[10px] font-black text-navy">DEMO</span>}
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATE_STYLE[booking.state] || "bg-stone-100 text-stone-600"}`}
                        data-testid={`${testid}-state`}>{STATE_LABEL[booking.state] || booking.state}</span>
                </div>
            </div>
            <div className="mt-3 space-y-2">
                {booking.offers.map((o, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-cream/60 border border-stone-100 px-3.5 py-2 text-sm">
                        <Plane className="h-3.5 w-3.5 text-saffron rotate-90" />
                        <span className="font-bold text-navy">{o.segments[0].from} → {o.segments[o.segments.length - 1].to}</span>
                        <span className="text-stone-500 text-xs">{o.airline} {o.flight_nos.join("·")} · {fmtDate(o.segments[0].depart)} {fmtTime(o.segments[0].depart)}</span>
                    </div>
                ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-stone-600">
                <span>{booking.pax.adults} adult{booking.pax.adults > 1 ? "s" : ""}{booking.pax.children ? `, ${booking.pax.children} child${booking.pax.children > 1 ? "ren" : ""}` : ""}{booking.pax.infants ? `, ${booking.pax.infants} infant` : ""}</span>
                <span className="font-bold text-navy">Total {inr(booking.pricing.total)}</span>
                <span>Payment: <b className={booking.payment.status === "paid" ? "text-green-700" : "text-stone-500"}>{booking.payment.status.replace("_", " ")}</b></span>
            </div>
            {booking.state === "TICKETED" && (
                <div className="mt-3 rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-sm" data-testid={`${testid}-pnr`}>
                    <p className="font-bold text-green-800 flex items-center gap-2"><Ticket className="h-4 w-4" />PNR: {booking.pnr}</p>
                    <p className="text-green-700 text-xs mt-0.5">Ticket no(s): {booking.ticket_numbers.join(", ")}</p>
                </div>
            )}
            {booking.history?.length > 0 && (
                <div className="mt-3 border-t border-stone-100 pt-3">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-stone-400 mb-1.5">Progress</p>
                    <div className="space-y-1">
                        {booking.history.slice(-4).map((h, i) => (
                            <p key={i} className="text-xs text-stone-500 flex gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-saffron mt-1.5 shrink-0" />
                                {STATE_LABEL[h.state] || h.state} · {fmtDate(h.at)}
                            </p>
                        ))}
                    </div>
                </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
                {canPay && (
                    <button data-testid={`${testid}-pay`} onClick={payNow} disabled={payBusy}
                        className="rounded-full bg-saffron px-5 py-1.5 text-xs font-bold text-white hover:bg-navy transition-colors disabled:opacity-50 flex items-center gap-1.5">
                        {payBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />}
                        Pay {inr(booking.pricing.total)}
                    </button>
                )}
                {booking.cancellable && (
                    <button data-testid={`${testid}-cancel`} onClick={cancel} disabled={busy}
                        className="rounded-full border border-red-200 px-4 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5">
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />} Request cancellation
                    </button>
                )}
                <a href={`https://wa.me/919930007445?text=${encodeURIComponent(`Hi! I need help with my flight booking ${booking.reference}`)}`}
                    target="_blank" rel="noreferrer" data-testid={`${testid}-support`}
                    className="rounded-full border border-stone-200 px-4 py-1.5 text-xs font-bold text-navy hover:border-saffron flex items-center gap-1.5">
                    <MessageCircle className="h-3.5 w-3.5 text-saffron" /> Contact support
                </a>
            </div>
        </div>
    );
}

function LookupTab() {
    const [form, setForm] = useState({ reference: "", contact: "" });
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setResult(null);
        try { setResult((await lookupFlightBooking(form)).booking); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <form onSubmit={submit} className="space-y-3" data-testid="lookup-form">
            <input data-testid="lookup-ref" placeholder="Booking reference (GC-FLT-XXXXXX)" value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value.toUpperCase() })}
                className="w-full rounded-xl border border-stone-300 px-4 py-3 text-sm font-semibold text-navy" />
            <input data-testid="lookup-contact" placeholder="Email or mobile used while booking" value={form.contact}
                onChange={(e) => setForm({ ...form, contact: e.target.value })}
                className="w-full rounded-xl border border-stone-300 px-4 py-3 text-sm font-semibold text-navy" />
            <button data-testid="lookup-submit" disabled={busy}
                className="w-full rounded-full bg-navy text-cream font-display font-black py-3 hover:bg-saffron transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4" /> Find my booking</>}
            </button>
            {result && (
                <div className="pt-4">
                    <BookingCard booking={result} testid="lookup-booking"
                        onCancel={() => cancelFlightBooking(result.reference, { contact: form.contact }).then((r) => { setResult(r.booking); return r; })}
                        onPaid={(b) => setResult(b)} />
                </div>
            )}
        </form>
    );
}

function LoginTab({ onAuth }) {
    const [mode, setMode] = useState("login");
    const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
    const [busy, setBusy] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            const r = mode === "login"
                ? await customerLogin({ email: form.email, password: form.password })
                : await customerRegister(form);
            setCustomerToken(r.access_token);
            onAuth(r.customer);
            toast.success(`Welcome${r.customer?.name ? `, ${r.customer.name}` : ""}!`);
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const forgot = async () => {
        if (!/.+@.+\..+/.test(form.email)) { toast.error("Enter your email first"); return; }
        try { await customerForgot(form.email); setForgotSent(true); }
        catch { toast.error("Could not send the reset email right now"); }
    };
    return (
        <form onSubmit={submit} className="space-y-3" data-testid="cust-login-form">
            <div className="flex rounded-full border border-stone-300 overflow-hidden text-xs font-bold">
                {[["login", "Log in"], ["register", "Create account"]].map(([k, l]) => (
                    <button type="button" key={k} data-testid={`cust-mode-${k}`} onClick={() => setMode(k)}
                        className={`flex-1 py-2 ${mode === k ? "bg-navy text-cream" : "text-stone-500"}`}>{l}</button>
                ))}
            </div>
            {mode === "register" && (
                <>
                    <input data-testid="cust-name" placeholder="Full name" value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full rounded-xl border border-stone-300 px-4 py-3 text-sm font-semibold text-navy" />
                    <input data-testid="cust-phone" placeholder="Mobile (10 digits)" value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                        className="w-full rounded-xl border border-stone-300 px-4 py-3 text-sm font-semibold text-navy" />
                </>
            )}
            <input data-testid="cust-email" type="email" placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-xl border border-stone-300 px-4 py-3 text-sm font-semibold text-navy" />
            <input data-testid="cust-password" type="password" placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full rounded-xl border border-stone-300 px-4 py-3 text-sm font-semibold text-navy" />
            <button data-testid="cust-auth-submit" disabled={busy}
                className="w-full rounded-full bg-saffron text-white font-display font-black py-3 hover:bg-navy transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4" /> {mode === "login" ? "Log in" : "Create my account"}</>}
            </button>
            {mode === "login" && (
                <p className="text-center text-xs text-stone-500">
                    {forgotSent ? "If the email is registered, a reset link is on its way."
                        : <button type="button" onClick={forgot} className="text-saffron font-bold hover:underline" data-testid="cust-forgot">Forgot password?</button>}
                </p>
            )}
        </form>
    );
}

function OtpTab({ onAuth }) {
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);
    const request = async () => {
        if (!/^\d{10}$/.test(phone)) { toast.error("Enter a valid 10-digit mobile"); return; }
        setBusy(true);
        try { await customerOtpRequest(phone); setSent(true); toast.success("OTP sent on WhatsApp"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const verify = async () => {
        setBusy(true);
        try {
            const r = await customerOtpVerify({ phone, otp });
            setCustomerToken(r.access_token);
            onAuth(r.customer);
            toast.success("Logged in!");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div className="space-y-3" data-testid="otp-form">
            <div className="flex gap-2">
                <input data-testid="otp-phone" placeholder="WhatsApp mobile (10 digits)" value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    className="flex-1 rounded-xl border border-stone-300 px-4 py-3 text-sm font-semibold text-navy" />
                <button onClick={request} disabled={busy} data-testid="otp-send"
                    className="rounded-xl bg-navy text-cream px-4 text-sm font-bold hover:bg-saffron transition-colors disabled:opacity-60">
                    {sent ? "Resend" : "Send OTP"}
                </button>
            </div>
            {sent && (
                <>
                    <input data-testid="otp-code" placeholder="6-digit OTP" value={otp} maxLength={6}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                        className="w-full rounded-xl border border-stone-300 px-4 py-3 text-center text-lg font-display font-black tracking-[0.5em] text-navy" />
                    <button onClick={verify} disabled={busy || otp.length !== 6} data-testid="otp-verify"
                        className="w-full rounded-full bg-saffron text-white font-display font-black py-3 hover:bg-navy transition-colors disabled:opacity-60">
                        {busy ? "Verifying…" : "Verify & log in"}
                    </button>
                    <p className="text-[11px] text-stone-400 text-center flex items-center justify-center gap-1">
                        <ShieldCheck className="h-3 w-3" /> OTP arrives on WhatsApp and expires in 10 minutes.
                    </p>
                </>
            )}
        </div>
    );
}

export default function MyTripsPage() {
    const [params] = useSearchParams();
    const resetToken = params.get("reset");
    const [tab, setTab] = useState("lookup");
    const [customer, setCustomer] = useState(null);
    const [bookings, setBookings] = useState(null);
    const [checking, setChecking] = useState(!!getCustomerToken());
    const [resetForm, setResetForm] = useState({ password: "", done: false });

    const loadBookings = async () => {
        try { setBookings((await customerBookings(getCustomerToken())).bookings); }
        catch { setBookings([]); }
    };

    useEffect(() => {
        if (!getCustomerToken()) { setChecking(false); return; }
        customerMe(getCustomerToken())
            .then((c) => { setCustomer(c); loadBookings(); })
            .catch(() => setCustomerToken(""))
            .finally(() => setChecking(false));
    }, []);

    const onAuth = (c) => { setCustomer(c); loadBookings(); };

    const doReset = async () => {
        try {
            await customerReset({ token: resetToken, password: resetForm.password });
            setResetForm({ ...resetForm, done: true });
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    if (resetToken) {
        return (
            <div className="min-h-screen bg-cream"><Header />
                <main className="max-w-md mx-auto px-4 py-16" data-testid="reset-page">
                    <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-xl">
                        <h1 className="font-display font-black text-2xl text-navy">Set a new password</h1>
                        {resetForm.done ? (
                            <p className="mt-3 text-sm text-green-700 font-semibold" data-testid="reset-done">
                                Password updated! <Link to="/my-trips" className="text-saffron underline">Log in</Link> with your new password.
                            </p>
                        ) : (
                            <div className="mt-4 space-y-3">
                                <input type="password" data-testid="reset-password" placeholder="New password (min 8 characters)"
                                    value={resetForm.password} onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })}
                                    className="w-full rounded-xl border border-stone-300 px-4 py-3 text-sm font-semibold text-navy" />
                                <button onClick={doReset} data-testid="reset-submit"
                                    className="w-full rounded-full bg-saffron text-white font-display font-black py-3">Update password</button>
                            </div>
                        )}
                    </div>
                </main><Footer /></div>
        );
    }

    return (
        <div className="min-h-screen bg-cream" data-testid="my-trips-page">
            <Header />
            <section className="bg-navy text-cream">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
                    <p className="font-script text-3xl text-gold">Apne safar, ek jagah</p>
                    <h1 className="mt-1 font-display font-black text-4xl sm:text-5xl">My Trips</h1>
                    <p className="mt-2 text-cream/75 text-sm sm:text-base">Track flight bookings, tickets, refunds — all in one place.</p>
                </div>
            </section>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
                {checking ? (
                    <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-saffron" /></div>
                ) : customer ? (
                    <div>
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                            <div>
                                <p className="font-display font-black text-xl text-navy" data-testid="cust-greeting">Namaste, {customer.name}</p>
                                <p className="text-xs text-stone-500">{customer.email || customer.phone}</p>
                            </div>
                            <button data-testid="cust-logout" onClick={() => { setCustomerToken(""); setCustomer(null); setBookings(null); }}
                                className="rounded-full border border-stone-300 px-4 py-2 text-xs font-bold text-stone-500 hover:border-red-300 hover:text-red-600 flex items-center gap-1.5">
                                <LogOut className="h-3.5 w-3.5" /> Log out
                            </button>
                        </div>
                        {bookings === null ? (
                            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>
                        ) : bookings.length === 0 ? (
                            <div className="rounded-2xl border border-stone-200 bg-white p-10 text-center" data-testid="no-bookings">
                                <Plane className="h-10 w-10 text-stone-300 mx-auto" />
                                <p className="mt-3 font-display font-black text-lg text-navy">No trips yet</p>
                                <p className="text-sm text-stone-500 mt-1">When you book flights with us, they'll show up here.</p>
                                <Link to="/flights" className="mt-5 inline-block rounded-full bg-saffron px-6 py-2.5 font-bold text-white" data-testid="no-bookings-cta">Search flights</Link>
                            </div>
                        ) : (
                            <div className="space-y-4" data-testid="cust-bookings">
                                {bookings.map((b) => (
                                    <BookingCard key={b.reference} booking={b} testid={`my-booking-${b.reference}`}
                                        onCancel={() => cancelFlightBooking(b.reference, {}, getCustomerToken()).then((r) => {
                                            setBookings((bs) => bs.map((x) => (x.reference === b.reference ? r.booking : x))); return r;
                                        })}
                                        onPaid={(upd) => setBookings((bs) => bs.map((x) => (x.reference === upd.reference ? upd : x)))} />
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="max-w-lg mx-auto">
                        <div className="flex rounded-full border border-stone-300 overflow-hidden text-xs font-bold mb-5" data-testid="access-tabs">
                            {[["lookup", "Find a booking"], ["login", "Email login"], ["otp", "Mobile OTP"]].map(([k, l]) => (
                                <button key={k} data-testid={`access-tab-${k}`} onClick={() => setTab(k)}
                                    className={`flex-1 py-2.5 ${tab === k ? "bg-navy text-cream" : "text-stone-500 hover:text-navy"}`}>{l}</button>
                            ))}
                        </div>
                        <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-xl">
                            {tab === "lookup" && <LookupTab />}
                            {tab === "login" && <LoginTab onAuth={onAuth} />}
                            {tab === "otp" && <OtpTab onAuth={onAuth} />}
                        </div>
                        <p className="mt-4 text-center text-xs text-stone-400">
                            Logged-in travellers see every booking made with their email or mobile — no reference needed.
                        </p>
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
}
